import type { JSONContent } from "@tiptap/core";
import type {
  BookExportBookMetadata,
  BookExportCapabilityMatrix,
  BookExportConfig,
  BookExportJob,
  BookExportJobStatus,
  BookExportProjectIdentity,
  BookExportSummary,
  RenderedBookExportTarget,
} from "@/lib/book-assembly/export-contract";
import type { SubscriptionTier } from "@/types/projects";

export type BookAssemblyStep =
  | "chapter-selection"
  | "side-story-arrangement"
  | "package-preview"
  | "export-settings"
  | "generate";
export type SideStoryPlacementKind = "before-chapter" | "after-chapter" | "appendix";
export type OrderedAssemblySourceKind = "chapter" | "side_story";
export type BookAssemblyAvailabilityCode =
  | "available"
  | "no-main-story"
  | "continuous-main-story"
  | "no-chapters"
  | "project-not-found";

export interface BookAssemblyAvailability {
  code: BookAssemblyAvailabilityCode;
  eligible: boolean;
  reason: string | null;
  requiredTier?: SubscriptionTier;
  tierLocked?: boolean;
}

export interface BookAssemblySourceChapter {
  body: JSONContent;
  id: string;
  imagePath: string | null;
  isClosing: boolean;
  isOpening: boolean;
  sortOrder: number;
  status: "draft" | "in_review" | "final";
  statusLabel: string;
  title: string;
  updatedAt: string;
}

export interface BookAssemblySourceSideStory {
  attachmentPaths: string[];
  body: JSONContent;
  id: string;
  summary: string;
  title: string;
  updatedAt: string;
}

export interface BookAssemblySourceView {
  availability: BookAssemblyAvailability;
  bookMetadata: BookExportBookMetadata;
  capabilities: BookExportCapabilityMatrix;
  chapters: BookAssemblySourceChapter[];
  exportIdentity?: BookExportProjectIdentity;
  jobs?: BookExportJob[];
  mainStoryId: string | null;
  mainStoryMode: "chapters" | "continuous" | null;
  projectId: string;
  projectTitle: string;
  sideStories: BookAssemblySourceSideStory[];
  tier: SubscriptionTier;
}

export interface MainChapterSelection {
  chapterId: string;
  chapterNumber: number;
  included: boolean;
  isClosing: boolean;
  isOpening: boolean;
  locked: boolean;
  statusLabel: string;
  title: string;
}

export interface SideStoryPlacementChoice {
  anchorChapterId: string | null;
  included: boolean;
  placementKind: SideStoryPlacementKind;
  storyId: string;
  summary: string;
  title: string;
}

export interface SideStoryPlacementSuggestionCitation {
  openHref?: string | null;
  reason: string;
  secondaryText: string;
  sourceKind: "chapter" | "side_story" | "timeline_event";
  sourceLabel: string;
  sourceRecordId: string;
}

export interface SideStoryPlacementSuggestion {
  anchorChapterId: string | null;
  citations: SideStoryPlacementSuggestionCitation[];
  rationaleText: string;
  storyId: string;
  suggestedPlacementKind: SideStoryPlacementKind;
}

export interface SideStoryPlacementSuggestionSet {
  creditCost: number;
  creditsRemaining: number;
  suggestions: SideStoryPlacementSuggestion[];
  tier: SubscriptionTier;
}

export interface OrderedAssemblyItem {
  appendix: boolean;
  locked: boolean;
  sourceId: string;
  sourceKind: OrderedAssemblySourceKind;
  title: string;
}

export interface BookExportSettings {
  authorName: string;
  authorNote: string;
  bookTitle: string;
  bookSubtitle: string;
  coverAssetPath: string | null;
  coverFileName: string | null;
  coverPreviewUrl: string | null;
  dedication: string;
  seriesName: string;
  specialThanks: string;
  volumeLabel: string;
}

export interface BookAssemblyResult {
  assemblyId: string;
  downloadUrl: string;
  expiresInSeconds: number;
  fileName: string;
  generatedAt: string;
  jobId?: string;
  target: RenderedBookExportTarget;
}

export interface BookAssemblyRunState {
  errorCode: string | null;
  errorMessage: string | null;
  progressPercent: number;
  result: BookAssemblyResult | null;
  status:
    | "idle"
    | "validating"
    | "assembling"
    | "rendering"
    | "saving"
    | "failed"
    | "succeeded";
}

export interface BookAssemblyDraft {
  exportConfig?: BookExportConfig;
  jobs?: BookExportJob[];
  orderedItems: OrderedAssemblyItem[];
  projectId: string;
  run: BookAssemblyRunState;
  selectedChapterIds: string[];
  settings: BookExportSettings;
  sideStoryPlacements: SideStoryPlacementChoice[];
  summary?: BookExportSummary;
  step: BookAssemblyStep;
  suggestionSet: SideStoryPlacementSuggestionSet | null;
}

export interface BookAssemblyLaunchState {
  availability: BookAssemblyAvailability;
  chapterCount: number;
  href: string;
  latestJobStatus?: BookExportJobStatus | null;
  recommendedUpgradeTier: SubscriptionTier | null;
  sideStoryCount: number;
  tier: SubscriptionTier;
}

export const bookAssemblySteps: BookAssemblyStep[] = [
  "chapter-selection",
  "side-story-arrangement",
  "package-preview",
  "export-settings",
  "generate",
];

export const sideStoryPlacementKinds: SideStoryPlacementKind[] = [
  "before-chapter",
  "after-chapter",
  "appendix",
];

export const sideStoryPlacementLabels: Record<SideStoryPlacementKind, string> = {
  "before-chapter": "Before chapter",
  "after-chapter": "After chapter",
  appendix: "Appendix",
};

export function createDefaultBookExportSettings(
  projectTitle: string,
  overrides: Partial<BookExportSettings> = {},
): BookExportSettings {
  return {
    authorName: "",
    authorNote: "",
    bookTitle: projectTitle,
    bookSubtitle: "",
    coverAssetPath: null,
    coverFileName: null,
    coverPreviewUrl: null,
    dedication: "",
    seriesName: "",
    specialThanks: "",
    volumeLabel: "",
    ...overrides,
  };
}

export function createIdleBookAssemblyRun(): BookAssemblyRunState {
  return {
    errorCode: null,
    errorMessage: null,
    progressPercent: 0,
    result: null,
    status: "idle",
  };
}

export {
  bookExportCurrentStages,
  bookExportJobStatuses,
  bookExportMessageSources,
  bookExportModes,
  bookExportPageSizes,
  bookExportPresets,
  bookExportReportEntryKinds,
  bookExportSectionKeys,
  bookExportTargets,
  createDefaultBookExportConfig,
  isBookExportEndMatterMessageSource,
  isBookExportFrontMatterMessageSource,
  isBookExportMode,
  isBookExportPageSize,
  isBookExportPreset,
  isBookExportTarget,
  type BookExportArtifact,
  type BookExportBookMetadata,
  type BookExportCapabilityMatrix,
  type BookExportConfig,
  type BookExportCurrentStage,
  type BookExportEndMatterMessageSource,
  type BookExportFrontMatterMessageSource,
  type BookExportJob,
  type BookExportJobStatus,
  type BookExportMainStoryChapter,
  type BookExportManifestSource,
  type BookExportMode,
  type BookExportPageSize,
  type BookExportPreset,
  type BookExportProjectIdentity,
  type BookExportProjectOverrides,
  type BookExportReport,
  type BookExportReportEntry,
  type BookExportReportEntryKind,
  type BookExportSectionKey,
  type BookExportSideStoryManifestEntry,
  type BookExportSummary,
  type BookExportSummarySection,
  type BookExportTarget,
  type BookExportTier,
  type CanonicalBookExportManifest,
  type RenderedBookExportTarget,
} from "@/lib/book-assembly/export-contract";
